import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, FileUp, Plus, Trash2, User, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DOCUMENT_SIGN_MAX_BULK_RECIPIENTS,
  DOCUMENT_SIGN_RECIPIENT_EMAIL_MAX_LENGTH,
  DOCUMENT_SIGN_RECIPIENT_NAME_MAX_LENGTH,
  DOCUMENT_SIGN_MAX_RECIPIENTS,
  type DocumentSignBulkRecipient,
  type DocumentSignRecipientResponse,
} from '@/lib/api';
import { useArcheSettings } from '@/lib/hooks/useArcheSettings';
import { useRecipients, useSetBulkRecipients, useSetRecipients } from '../hooks';
import { useRecentRecipients } from '../hooks/useRecentRecipients';
import { DEFAULT_DOCUMENT_SIGN_SETTINGS, DOCUMENT_SIGN_ARCHE_ID } from '../settings';
import { EACH_RECIPIENT_COLOR, getRecipientColor } from './recipientColors';
import { ContactPickerDialog } from './ContactPickerDialog';
import { BULK_CSV_MAX_BYTES, describeBulkCsvImport, parseBulkRecipientsCsv } from '../bulkCsv';
import { isRecipientEmail } from '../emailAddress';

interface DraftRecipient {
  /** Stable React key for the row's lifetime — never reassigned. */
  key: string;
  /** Server id once the row has been saved; null for a new, unsaved row. */
  recipientId: string | null;
  name: string;
  email: string;
  role: 'signer' | 'viewer' | 'approver';
  /** True once the server holds an access code for this recipient. */
  hasStoredCode: boolean;
}

/** One signer, as published upward for the access-code panel. */
export interface RecipientSignerSummary {
  key: string;
  name: string;
  email: string;
  hasStoredCode: boolean;
}

interface RecipientsTableProps {
  envelopeId: string;
  /**
   * Plaintext access codes entered on the Regulated tier, keyed by row key.
   *
   * Entered in `AccessCodeAssignment`, beneath the tier choice that creates
   * the requirement — NOT here. This table used to carry a per-row code
   * column, which put the control several sections above the warning that
   * announced it and wedged a credential field between Role and Delete. The
   * codes still travel with the recipient save, because they are written by
   * the same endpoint; only the input moved.
   */
  accessCodes?: Record<string, string>;
  /**
   * Called whenever the signer roster changes, so the access-code panel can
   * render a row per signer without waiting for a save. Fires on identity
   * changes only (key, name, email, stored-code state) — not on every
   * keystroke elsewhere in the table.
   */
  onSignersChange?: (signers: RecipientSignerSummary[]) => void;
  /**
   * Holder this envelope belongs to. Required for the recent-recipients
   * autofill — the suggestion list is scoped to the envelope's workspace.
   * Omit only in tests or contexts where the autofill isn't needed (the
   * suggest menu falls back to owner + Add-contact in that case).
   */
  envelopeHolderId?: string;
  /**
   * `'sequential'` numbers each row so the owner can verify the signing
   * order before sending; `'parallel'` (or omitted) hides the numbers
   * since order is meaningless when everyone can sign at any time.
   */
  signingOrder?: 'parallel' | 'sequential';
  /** When true, hide all editing affordances. Used once the envelope is sent. */
  readOnly?: boolean;
  /**
   * Bulk send: each row is a person who gets their OWN copy of the envelope,
   * as its only signer. Rows are saved to the envelope's bulk list instead of
   * as recipients, and role / order / access codes do not apply.
   *
   * The table is keyed on the send mode by its parent, so switching modes
   * remounts it and re-hydrates from the right source.
   */
  bulk?: boolean;
  /** The saved bulk list — the hydration source when `bulk` is set. */
  bulkRecipients?: DocumentSignBulkRecipient[] | null;
}

/**
 * Imperative handle the envelope builder uses to flush pending recipient
 * edits when the user navigates (Back / Review and send). `save` returns
 * false when a row fails validation — the builder blocks navigation on a
 * false result and leaves the inline error visible.
 */
export interface RecipientsTableHandle {
  save: () => Promise<boolean>;
}

/** Identifies which row + field currently has the suggestion menu open. */
interface ActiveSuggest {
  index: number;
  field: 'name' | 'email';
}

const ROLE_LABEL: Record<DraftRecipient['role'], string> = {
  signer: 'Signer',
  viewer: 'Viewer',
  approver: 'Approver',
};

let scratchKeyCounter = 0;
function nextScratchKey(): string {
  scratchKeyCounter += 1;
  return `new-${Date.now()}-${scratchKeyCounter}`;
}

function toDraft(r: DocumentSignRecipientResponse): DraftRecipient {
  return {
    key: r.recipientId,
    recipientId: r.recipientId,
    name: r.name ?? '',
    email: r.email ?? '',
    role: r.role,
    hasStoredCode: r.requiresAccessCode === true,
  };
}

/**
 * Why this row cannot be saved, as the end of a sentence ("needs a name"), or
 * null. A row with neither field filled is an unused row, not a problem.
 */
function draftProblem(d: { name: string; email: string }): string | null {
  const name = d.name.trim();
  const email = d.email.trim().toLowerCase();
  if (!name && !email) return null;
  if (!name) return 'needs a name';
  if (!email) return 'needs an email';
  if (!isRecipientEmail(email)) return 'needs a valid email';
  return null;
}

export const RecipientsTable = forwardRef<RecipientsTableHandle, RecipientsTableProps>(
  function RecipientsTable(
    {
      envelopeId,
      envelopeHolderId,
      signingOrder,
      accessCodes = {},
      onSignersChange,
      readOnly = false,
      bulk = false,
      bulkRecipients = null,
    },
    ref,
  ) {
    const isSequential = !bulk && signingOrder === 'sequential';
    const { recipients: recentRecipients } = useRecentRecipients(envelopeHolderId ?? null);
    const { recipients, loading, fetching } = useRecipients(envelopeId);
    const setRecipients = useSetRecipients(envelopeId);
    const setBulkRecipients = useSetBulkRecipients(envelopeId);
    const [dsSettings] = useArcheSettings(DOCUMENT_SIGN_ARCHE_ID, DEFAULT_DOCUMENT_SIGN_SETTINGS);

    const [drafts, setDrafts] = useState<DraftRecipient[]>([]);
    const [error, setError] = useState<string | null>(null);
    /**
     * Why the latest background save did not go through. Autosave is silent
     * (it must not interrupt typing), but a list that cannot save must not
     * look saved — an imported 300-person list sat unsaved behind one typed
     * row without an email, and closing the tab lost it.
     */
    const [unsaved, setUnsaved] = useState<string | null>(null);
    const [isDirty, setIsDirty] = useState(false);
    const [activeSuggest, setActiveSuggest] = useState<ActiveSuggest | null>(null);
    const [contactPickerRow, setContactPickerRow] = useState<number | null>(null);
    const errorRef = useRef<HTMLParagraphElement>(null);
    const hydratedRef = useRef(false);
    const draftsRef = useRef(drafts);
    /**
     * Codes this component has actually SENT, by row key.
     *
     * Access codes are entered outside this component (in the builder, under
     * the tier radio) but saved by it, so `isDirty` — which only tracks this
     * table's own edits — cannot see them. Without this, a sender whose
     * recipients had already auto-saved could type every code, click "Review
     * and send", and have `persist` short-circuit on `!isDirty` and return
     * true having sent nothing: the builder's gate passes (it reads local
     * state) but the server still has no codes, so Review blocks Send with no
     * way forward.
     */
    const persistedCodesRef = useRef<Record<string, string>>({});

    // Hydrate once from the server list. After that the component owns the
    // drafts — saves reconcile them from the mutation result, so the refetch
    // an auto-save triggers never clobbers in-progress edits.
    useEffect(() => {
      if (hydratedRef.current) return;
      if (bulk) {
        setDrafts(
          (bulkRecipients ?? []).map((p) => ({
            key: nextScratchKey(),
            recipientId: null,
            name: p.name,
            email: p.email,
            role: 'signer',
            hasStoredCode: false,
          })),
        );
        hydratedRef.current = true;
        return;
      }
      // Wait out any refetch in flight, not just the first load. After a mode
      // switch the table remounts while the recipient rows are refreshing;
      // hydrating from the pre-switch rows (the nameless placeholder) and then
      // autosaving would delete the people the switch had just converted.
      if (loading || fetching) return;
      setDrafts(recipients.map(toDraft));
      hydratedRef.current = true;
    }, [recipients, loading, fetching, bulk, bulkRecipients]);

    // Latest drafts, readable inside async callbacks without a stale closure.
    useEffect(() => {
      draftsRef.current = drafts;
    }, [drafts]);

    /**
     * Publish the signer roster upward for the access-code panel.
     *
     * Keyed on a signature string rather than on `drafts` so it fires when a
     * signer's identity changes and not on every keystroke in an unrelated
     * row — the parent stores the result in state, and an effect that ran on
     * every render would loop.
     *
     * Only signers: viewers and approvers never receive a code, and the server
     * rejects one on them.
     */
    // A bulk send cannot carry access codes, so it publishes no signers for
    // the access-code panel.
    const signers = drafts
      .filter((d) => !bulk && d.role === 'signer')
      // Same predicate `persist` uses to decide a row is real. An untouched
      // row added by "Add recipient" is dropped from the save payload, so
      // publishing it here produced a code field for "Unnamed signer" and a
      // "Review and send" gate demanding a code for a recipient that will
      // never exist server-side — unblockable except by deleting the row.
      .filter((d) => d.name.trim() !== '' || d.email.trim() !== '')
      .map((d) => ({
        key: d.key,
        name: d.name.trim(),
        email: d.email.trim(),
        hasStoredCode: d.hasStoredCode,
      }));
    const signerSignature = signers
      .map((s) => [s.key, s.name, s.email, s.hasStoredCode ? '1' : '0'].join('\u0000'))
      .join('\u0001');
    const signersRef = useRef(signers);
    signersRef.current = signers;
    const onSignersChangeRef = useRef(onSignersChange);
    onSignersChangeRef.current = onSignersChange;
    //
    // Depends on `signerSignature` alone. `signers` is a new array every
    // render, and `onSignersChange` is typically an inline arrow — either in
    // the dependency list would fire this on every render and loop against the
    // parent's setState. Both are read through refs instead. No
    // `eslint-disable` comment: this repo's config does not register
    // `react-hooks/exhaustive-deps`, so suppressing it is itself a lint error.
    useEffect(() => {
      onSignersChangeRef.current?.(signersRef.current);
    }, [signerSignature]);

    // The save is triggered from the page's Back / Review buttons, which can
    // sit far from this section — pull a validation error into view so a
    // blocked navigation never looks like nothing happened.
    useEffect(() => {
      if (error) {
        errorRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      }
    }, [error]);

    // The owner can self-fill a row from their Document Sign settings —
    // offered whenever a default email is set and they aren't already among
    // the recipients. A blank display name is fine (the option still shows,
    // labelled by email); with no default email there is no suggestion.
    const ownerName = dsSettings.defaultDisplayName.trim();
    const ownerEmail = dsSettings.defaultEmail.trim().toLowerCase();
    const ownerIsRecipient =
      ownerEmail !== '' && drafts.some((d) => d.email.trim().toLowerCase() === ownerEmail);
    const ownerOption =
      ownerEmail && !ownerIsRecipient ? { name: ownerName, email: ownerEmail } : null;

    /** Signer rows whose code differs from what was last sent. */
    const unsentCodeKeys = drafts
      .filter((d) => d.role === 'signer')
      .filter((d) => {
        const value = (accessCodes[d.key] ?? '').trim();
        return value !== '' && value !== persistedCodesRef.current[d.key];
      })
      .map((d) => d.key);
    const unsentCodeSignature = unsentCodeKeys.join('\u0001');

    function update(index: number, patch: Partial<DraftRecipient>) {
      setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));
      setIsDirty(true);
    }

    function remove(index: number) {
      setDrafts((prev) => prev.filter((_, i) => i !== index));
      setIsDirty(true);
    }

    function move(index: number, delta: -1 | 1) {
      setDrafts((prev) => {
        const target = index + delta;
        if (target < 0 || target >= prev.length) return prev;
        const next = [...prev];
        [next[index], next[target]] = [next[target], next[index]];
        return next;
      });
      setIsDirty(true);
    }

    /**
     * Two limits. A shared envelope holds DOCUMENT_SIGN_MAX_RECIPIENTS — a
     * real limit (every signer sees the others; one PDF carries every
     * signature). A bulk list holds DOCUMENT_SIGN_MAX_BULK_RECIPIENTS, however
     * people got onto it. Past DOCUMENT_SIGN_MAX_RECIPIENTS a bulk list is
     * shown compactly rather than as hundreds of inputs, and "Add person"
     * opens a small form instead of adding an input row — a display choice,
     * not a limit.
     */
    const listLimit = bulk ? DOCUMENT_SIGN_MAX_BULK_RECIPIENTS : DOCUMENT_SIGN_MAX_RECIPIENTS;
    const atLimit = drafts.length >= listLimit;
    const compactList = bulk && drafts.length > DOCUMENT_SIGN_MAX_RECIPIENTS;
    /** The quick-add form for a long bulk list; null when closed. */
    const [quickAdd, setQuickAdd] = useState<{ name: string; email: string } | null>(null);
    const [quickAddError, setQuickAddError] = useState<string | null>(null);
    const quickAddNameRef = useRef<HTMLInputElement>(null);
    const [importNotice, setImportNotice] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    async function importCsv(file: File) {
      setImportNotice(null);
      if (file.size > BULK_CSV_MAX_BYTES) {
        setError('That file is too large for a list of people. Use a CSV of names and emails.');
        return;
      }
      let text: string;
      try {
        text = await file.text();
      } catch {
        setError('That file could not be read. Try saving it as a CSV again.');
        return;
      }
      const result = parseBulkRecipientsCsv(
        text,
        // Every row the import keeps (below), so the list cannot pass the limit.
        draftsRef.current
          .filter((d) => d.name.trim() !== '' || d.email.trim() !== '')
          .map((d) => ({ name: d.name, email: d.email })),
        DOCUMENT_SIGN_MAX_BULK_RECIPIENTS,
      );
      setError(null);
      setImportNotice(describeBulkCsvImport(result, DOCUMENT_SIGN_MAX_BULK_RECIPIENTS));
      if (result.people.length === 0) return;
      setDrafts((prev) => [
        // Drop untouched blank rows — an import replaces the empty row a
        // sender clicked "Add" for, rather than sitting below it.
        ...prev.filter((d) => d.name.trim() !== '' || d.email.trim() !== ''),
        ...result.people.map((p) => ({
          key: nextScratchKey(),
          recipientId: null,
          name: p.name,
          email: p.email,
          role: 'signer' as const,
          hasStoredCode: false,
        })),
      ]);
      setIsDirty(true);
    }

    function add() {
      // The server refuses a list over the limit — and refuses the WHOLE
      // save, so every later edit would silently fail too. Stop the row being
      // added instead.
      if (draftsRef.current.length >= listLimit) return;
      // A long bulk list has no input rows to type into: add through the
      // quick-add form instead.
      if (bulk && draftsRef.current.length >= DOCUMENT_SIGN_MAX_RECIPIENTS) {
        setQuickAdd((open) => open ?? { name: '', email: '' });
        setQuickAddError(null);
        return;
      }
      setDrafts((prev) => [
        ...prev,
        {
          key: nextScratchKey(),
          recipientId: null,
          name: '',
          email: '',
          role: 'signer',
          hasStoredCode: false,
        },
      ]);
      setIsDirty(true);
    }

    /**
     * Add the person typed into the quick-add form. Checked against the same
     * rules a save applies (name, the shared email rule, length, duplicates),
     * so a person added here can never be the row that stops the list saving.
     * The form stays open for the next person.
     */
    function submitQuickAdd() {
      if (!quickAdd) return;
      const name = quickAdd.name.replace(/\s+/g, ' ').trim();
      const email = quickAdd.email.trim().toLowerCase();
      const problem = draftProblem({ name, email });
      if (!name || !email || problem) {
        setQuickAddError(`This person ${problem ?? (name ? 'needs an email' : 'needs a name')}.`);
        return;
      }
      if (draftsRef.current.some((d) => d.email.trim().toLowerCase() === email)) {
        setQuickAddError(`${email} is already on the list.`);
        return;
      }
      if (draftsRef.current.length >= listLimit) {
        setQuickAddError(`A bulk send can go to at most ${listLimit} people.`);
        return;
      }
      setDrafts((prev) => [
        ...prev,
        {
          key: nextScratchKey(),
          recipientId: null,
          name,
          email,
          role: 'signer',
          hasStoredCode: false,
        },
      ]);
      setIsDirty(true);
      setQuickAdd({ name: '', email: '' });
      setQuickAddError(null);
      quickAddNameRef.current?.focus();
    }

    function fillFromOwner(index: number) {
      update(index, { name: ownerName, email: ownerEmail });
      setActiveSuggest(null);
    }

    function fillFromRecent(index: number, recent: { name: string; email: string }) {
      update(index, { name: recent.name, email: recent.email });
      setActiveSuggest(null);
    }

    /**
     * Recent-recipient suggestions filtered against the current draft list
     * (no point offering someone who's already in the envelope). Computed
     * here so both Name and Email focus handlers offer the same set.
     */
    const draftEmails = new Set(
      drafts.map((d) => d.email.trim().toLowerCase()).filter((e) => e !== ''),
    );
    const recentSuggestions = recentRecipients.filter((r) => !draftEmails.has(r.email));

    function openContactPicker(index: number) {
      setActiveSuggest(null);
      setContactPickerRow(index);
    }

    /**
     * Flush pending edits. A `silent` background save skips the error surface
     * and never blocks; the navigation-triggered save (silent=false) shows
     * validation errors and returns false so the builder can block. Rows with
     * no input are dropped; every remaining row needs a name and a valid,
     * unique email. On success the rows are reconciled with their server ids.
     */
    const persist = useCallback(
      async (silent: boolean): Promise<boolean> => {
        // Unsent codes count as pending work even when no row was edited.
        if (!isDirty && unsentCodeSignature === '') return true;

        // A row with neither field touched is just an unused row — drop it.
        const entered = drafts.filter((d) => d.name.trim() !== '' || d.email.trim() !== '');
        const trimmed = entered.map((d) => ({
          ...d,
          name: d.name.trim(),
          email: d.email.trim().toLowerCase(),
        }));

        // A background save reports why it stopped as "not saved"; a save the
        // user is waiting on (leaving the page) shows it as the error.
        const refuse = (message: string): false => {
          if (silent) setUnsaved(message);
          else setError(message);
          return false;
        };

        for (const d of trimmed) {
          const problem = draftProblem(d);
          if (problem) return refuse(`${d.name || d.email || 'A row'} ${problem}.`);
        }
        const seenEmails = new Set<string>();
        for (const d of trimmed) {
          if (seenEmails.has(d.email)) return refuse(`Duplicate email: ${d.email}`);
          seenEmails.add(d.email);
        }

        if (trimmed.length > listLimit) {
          return refuse(
            `${bulk ? 'A bulk send can go to' : 'An envelope can have'} at most ${listLimit} ${
              bulk ? 'people' : 'recipients'
            }. Remove ${trimmed.length - listLimit} to continue.`,
          );
        }

        const snapshot = draftsRef.current;
        if (bulk) {
          try {
            await setBulkRecipients.mutateAsync(
              trimmed.map((d) => ({ name: d.name, email: d.email })),
            );
            setError(null);
            setUnsaved(null);
            if (draftsRef.current === snapshot) setIsDirty(false);
            return true;
          } catch (err) {
            return refuse(err instanceof Error ? err.message : 'Failed to save recipients');
          }
        }
        try {
          const saved = await setRecipients.mutateAsync(
            trimmed.map((d, i) => ({
              // Existing rows send their id so the server updates them in
              // place rather than recreating them (which cascade-deletes the
              // fields placed against them).
              recipientId: d.recipientId ?? undefined,
              name: d.name,
              email: d.email,
              role: d.role,
              routingOrder: i,
              // Omit rather than send empty: an empty string would clear a
              // code the sender never intended to touch. Only signers carry
              // one — the server rejects a code on a viewer or approver.
              ...(d.role === 'signer' && (accessCodes[d.key] ?? '').trim()
                ? { accessCode: (accessCodes[d.key] ?? '').trim() }
                : {}),
            })),
          );
          setError(null);
          setUnsaved(null);
          // Remember the codes that just went out, so a later save with no
          // further changes short-circuits again instead of re-sending them.
          for (const d of trimmed) {
            const sent = (accessCodes[d.key] ?? '').trim();
            if (d.role === 'signer' && sent) persistedCodesRef.current[d.key] = sent;
          }
          // Backfill server ids onto the saved rows (saved is in payload
          // order); the React keys stay stable so focused inputs don't remount.
          setDrafts((prev) => {
            let k = 0;
            return prev.map((d) => {
              if (d.name.trim() === '' && d.email.trim() === '') return d;
              const row = saved[k];
              k += 1;
              return row
                ? {
                    ...d,
                    recipientId: row.recipientId,
                    // Reflect the code that just landed, so the panel switches
                    // from "required" to "already set" without a refetch.
                    hasStoredCode: row.requiresAccessCode === true || d.hasStoredCode,
                  }
                : d;
            });
          });
          // Clear dirty only if nothing was edited during the in-flight save.
          if (draftsRef.current === snapshot) setIsDirty(false);
          return true;
        } catch (err) {
          return refuse(err instanceof Error ? err.message : 'Failed to save recipients');
        }
      },
      [
        drafts,
        isDirty,
        setRecipients,
        setBulkRecipients,
        bulk,
        listLimit,
        accessCodes,
        unsentCodeSignature,
      ],
    );

    const save = useCallback(() => persist(false), [persist]);

    useImperativeHandle(ref, () => ({ save }), [save]);

    // A ref to the latest persist, so the debounce effect needn't depend on it
    // (the mutation object changes identity on every render).
    const persistRef = useRef(persist);
    useEffect(() => {
      persistRef.current = persist;
    }, [persist]);

    // Auto-save shortly after edits settle so later steps (field placement)
    // see recipients without waiting for navigation. Silent — validation
    // errors surface only on the navigation-triggered save.
    useEffect(() => {
      // Codes are pending work too — without them in this condition an
      // entered code would sit unsaved until some unrelated row edit happened
      // to trigger the debounce.
      if (!isDirty && unsentCodeSignature === '') return;
      const timer = setTimeout(() => {
        void persistRef.current(true);
      }, 700);
      return () => clearTimeout(timer);
    }, [drafts, isDirty, unsentCodeSignature]);

    if (!bulk && !hydratedRef.current) {
      return <p className="text-sm text-muted-foreground">Loading recipients…</p>;
    }

    return (
      <div className="space-y-3">
        {drafts.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {bulk ? 'No one added yet.' : 'No recipients yet.'}
          </p>
        ) : compactList ? (
          <ul
            className="max-h-96 overflow-y-auto divide-y rounded-md border text-sm"
            aria-label="People to send to"
          >
            {drafts.map((draft, i) => (
              <li key={draft.key} className="flex items-center justify-between gap-3 px-3 py-1.5">
                <span className="min-w-0 truncate">
                  <span className="font-medium">{draft.name || draft.email}</span>
                  {draft.name && <span className="text-muted-foreground"> · {draft.email}</span>}
                  {/* A row the list cannot be saved with is shown, not hidden —
                      in a long list it is otherwise impossible to find. */}
                  {draftProblem(draft) && (
                    <span className="text-destructive"> — {draftProblem(draft)}</span>
                  )}
                </span>
                {!readOnly && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => remove(i)}
                    aria-label={`Remove ${draft.name || draft.email}`}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <ul className="space-y-2">
            {drafts.map((draft, i) => {
              // On a bulk send every person gets the same fields, so they
              // share the "Each recipient" color the field editor uses.
              const color = bulk ? EACH_RECIPIENT_COLOR : getRecipientColor(i);
              return (
                <li
                  key={draft.key}
                  className="rounded-md border bg-card p-3 space-y-3 sm:space-y-0 sm:flex sm:items-end sm:gap-3"
                >
                  <span
                    className="hidden sm:block h-9 w-2 rounded-sm"
                    aria-hidden="true"
                    style={{ background: color.swatch }}
                  />
                  {/*
                   * Sequential-order badge. Surfaced only when "One at a
                   * time" is selected so the owner can verify the order
                   * matches their intent before clicking Send. Parallel
                   * envelopes skip this — order is meaningless there.
                   */}
                  {isSequential && (
                    <span
                      className="hidden sm:flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary"
                      aria-label={`Sign order ${i + 1}`}
                      title="Sign order"
                    >
                      {i + 1}
                    </span>
                  )}
                  <div className="flex-1 space-y-1">
                    <Label htmlFor={`recipient-name-${draft.key}`} className="text-xs">
                      Name
                    </Label>
                    <div className="relative">
                      <Input
                        id={`recipient-name-${draft.key}`}
                        value={draft.name}
                        maxLength={DOCUMENT_SIGN_RECIPIENT_NAME_MAX_LENGTH}
                        onChange={(e) => update(i, { name: e.target.value })}
                        onFocus={() => setActiveSuggest({ index: i, field: 'name' })}
                        onBlur={() => setActiveSuggest(null)}
                        disabled={readOnly}
                      />
                      {activeSuggest?.index === i &&
                        activeSuggest.field === 'name' &&
                        draft.name.trim() === '' && (
                          <RecipientSuggestMenu
                            owner={ownerOption}
                            recent={recentSuggestions}
                            onPickOwner={() => fillFromOwner(i)}
                            onPickRecent={(r) => fillFromRecent(i, r)}
                            onAddContact={() => openContactPicker(i)}
                          />
                        )}
                    </div>
                  </div>
                  <div className="flex-1 space-y-1">
                    <Label htmlFor={`recipient-email-${draft.key}`} className="text-xs">
                      Email
                    </Label>
                    <div className="relative">
                      <Input
                        id={`recipient-email-${draft.key}`}
                        type="email"
                        value={draft.email}
                        maxLength={DOCUMENT_SIGN_RECIPIENT_EMAIL_MAX_LENGTH}
                        onChange={(e) => update(i, { email: e.target.value })}
                        onFocus={() => setActiveSuggest({ index: i, field: 'email' })}
                        onBlur={() => setActiveSuggest(null)}
                        disabled={readOnly}
                      />
                      {activeSuggest?.index === i &&
                        activeSuggest.field === 'email' &&
                        draft.email.trim() === '' && (
                          <RecipientSuggestMenu
                            owner={ownerOption}
                            recent={recentSuggestions}
                            onPickOwner={() => fillFromOwner(i)}
                            onPickRecent={(r) => fillFromRecent(i, r)}
                            onAddContact={() => openContactPicker(i)}
                          />
                        )}
                    </div>
                  </div>
                  {!bulk && (
                    <div className="w-full sm:w-32 space-y-1">
                      <Label htmlFor={`recipient-role-${draft.key}`} className="text-xs">
                        Role
                      </Label>
                      <Select
                        value={draft.role}
                        onValueChange={(value) =>
                          update(i, { role: value as DraftRecipient['role'] })
                        }
                        disabled={readOnly}
                      >
                        <SelectTrigger id={`recipient-role-${draft.key}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(['signer', 'viewer', 'approver'] as const).map((role) => (
                            <SelectItem key={role} value={role}>
                              {ROLE_LABEL[role]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {!readOnly && (
                    <div className="flex gap-1">
                      {!bulk && (
                        <>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => move(i, -1)}
                            disabled={i === 0}
                            aria-label="Move up"
                          >
                            <ChevronUp className="h-4 w-4" aria-hidden="true" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => move(i, 1)}
                            disabled={i === drafts.length - 1}
                            aria-label="Move down"
                          >
                            <ChevronDown className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => remove(i)}
                        aria-label="Remove recipient"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {error && (
          <p ref={errorRef} className="text-sm text-destructive">
            {error}
          </p>
        )}

        {unsaved && !error && (
          <p className="text-sm text-amber-700 dark:text-amber-400" role="status">
            Not saved yet: {unsaved}
          </p>
        )}

        {importNotice && (
          <p className="text-xs text-muted-foreground" role="status">
            {importNotice}
          </p>
        )}

        {!readOnly && quickAdd && (
          <form
            className="rounded-md border bg-card p-3 space-y-2"
            aria-label="Add a person"
            // The shared email rule decides, not the browser's own (which
            // differs, and would block the submit with its own tooltip).
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              submitQuickAdd();
            }}
          >
            <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto_auto] sm:items-end">
              <div className="space-y-1">
                <Label htmlFor="ds-quick-add-name">Name</Label>
                <Input
                  id="ds-quick-add-name"
                  ref={quickAddNameRef}
                  autoFocus
                  value={quickAdd.name}
                  maxLength={DOCUMENT_SIGN_RECIPIENT_NAME_MAX_LENGTH}
                  onChange={(e) => setQuickAdd({ ...quickAdd, name: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ds-quick-add-email">Email</Label>
                <Input
                  id="ds-quick-add-email"
                  type="email"
                  value={quickAdd.email}
                  maxLength={DOCUMENT_SIGN_RECIPIENT_EMAIL_MAX_LENGTH}
                  onChange={(e) => setQuickAdd({ ...quickAdd, email: e.target.value })}
                />
              </div>
              <Button type="submit" size="sm" disabled={atLimit}>
                Add
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setQuickAdd(null);
                  setQuickAddError(null);
                }}
              >
                Done
              </Button>
            </div>
            {quickAddError && (
              <p className="text-xs text-destructive" role="alert">
                {quickAddError}
              </p>
            )}
          </form>
        )}

        {!readOnly && (
          <div className="space-y-1">
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" onClick={add} disabled={atLimit}>
                <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
                {bulk ? 'Add person' : 'Add recipient'}
              </Button>
              {bulk && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={atLimit}
                  >
                    <FileUp className="h-4 w-4 mr-1" aria-hidden="true" />
                    Import CSV
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    data-testid="bulk-csv-input"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (file) void importCsv(file);
                    }}
                  />
                </>
              )}
            </div>
            {bulk ? (
              <p
                className={
                  atLimit
                    ? 'text-xs text-amber-700 dark:text-amber-300'
                    : 'text-xs text-muted-foreground'
                }
                role={atLimit ? 'status' : undefined}
              >
                {drafts.length} of {DOCUMENT_SIGN_MAX_BULK_RECIPIENTS} people
                {atLimit && " — that's the limit for one bulk send."}
              </p>
            ) : atLimit ? (
              <p className="text-xs text-amber-700 dark:text-amber-300" role="status">
                {drafts.length} of {DOCUMENT_SIGN_MAX_RECIPIENTS} recipients — that's the limit per
                envelope.
              </p>
            ) : drafts.length > 0 ? (
              <p className="text-xs text-muted-foreground">
                {drafts.length} of {DOCUMENT_SIGN_MAX_RECIPIENTS} recipients
              </p>
            ) : null}
            {bulk && (
              <p className="text-xs text-muted-foreground">
                CSV: one person per row, with a Name and an Email column.
              </p>
            )}
          </div>
        )}

        <ContactPickerDialog
          open={contactPickerRow !== null}
          onOpenChange={(next) => {
            if (!next) setContactPickerRow(null);
          }}
          onPick={(picked) => {
            if (contactPickerRow !== null) {
              update(contactPickerRow, { name: picked.name, email: picked.email });
            }
          }}
        />
      </div>
    );
  },
);

// ---------------------------------------------------------------------------
// Field suggestion menu
// ---------------------------------------------------------------------------

interface RecipientSuggestMenuProps {
  /** The owner self-fill entry, or null when the owner is already a recipient. */
  owner: { name: string; email: string } | null;
  /**
   * Distinct recipients the workspace has previously sent envelopes to,
   * most-recently-used first, already filtered against the current draft
   * list. May be empty (e.g. workspace's first envelope, or every prior
   * recipient is already added).
   */
  recent: ReadonlyArray<{ name: string; email: string }>;
  onPickOwner: () => void;
  onPickRecent: (recent: { name: string; email: string }) => void;
  onAddContact: () => void;
}

/**
 * Dropdown shown under an empty Name/Email field. `onMouseDown` is suppressed
 * so clicking an entry doesn't blur the field before the click registers.
 *
 * Order: owner self-fill (if applicable), recent-recipient history (capped),
 * Add Contact. Recent history sits between the always-available actions so
 * an obvious self-fill stays at the top and "Add Contact" stays anchored at
 * the bottom where users expect a CTA.
 */
function RecipientSuggestMenu({
  owner,
  recent,
  onPickOwner,
  onPickRecent,
  onAddContact,
}: RecipientSuggestMenuProps) {
  // Cap the recent list so the dropdown stays scannable. Anything beyond
  // ~5 entries pushes Add Contact off the visible viewport on smaller
  // builder windows; the backend caps at 20 and we just take the first
  // few here.
  const visibleRecent = recent.slice(0, 5);
  return (
    <ul
      onMouseDown={(e) => e.preventDefault()}
      className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-md border bg-popover py-1 shadow-md"
    >
      {owner && (
        <li>
          <button
            type="button"
            onClick={onPickOwner}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent"
          >
            <User className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="min-w-0 truncate">
              {owner.name ? (
                <>
                  <span className="font-medium text-foreground">{owner.name}</span>
                  <span className="text-muted-foreground">: {owner.email}</span>
                </>
              ) : (
                <span className="font-medium text-foreground">{owner.email}</span>
              )}
            </span>
          </button>
        </li>
      )}
      {visibleRecent.length > 0 && (
        <>
          {(owner || visibleRecent.length > 0) && (
            <li className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              Recently used
            </li>
          )}
          {visibleRecent.map((r) => (
            <li key={r.email}>
              <button
                type="button"
                onClick={() => onPickRecent(r)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent"
              >
                <User className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="min-w-0 truncate">
                  <span className="font-medium text-foreground">{r.name}</span>
                  <span className="text-muted-foreground">: {r.email}</span>
                </span>
              </button>
            </li>
          ))}
        </>
      )}
      <li>
        <button
          type="button"
          onClick={onAddContact}
          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent"
        >
          <UserPlus className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          Add Contact
        </button>
      </li>
    </ul>
  );
}
